import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { expirationStatus, expirationStyle, expirationLabel } from '../utils/supplies';

const WRITE_ROLES = ['super_admin', 'regional_manager', 'building_admin'];

// Empty form template — keeps Date Added defaulting to today.
const blankForm = () => ({
  item_name: '',
  category: '',
  quantity: 0,
  unit: '',
  storage_location: '',
  lot_number: '',
  date_added: format(new Date(), 'yyyy-MM-dd'),
  expiration_date: '',
  reorder_threshold: '',
  notes: '',
});

export default function FacilitySupplies() {
  const { id: facilityId } = useParams();
  const { user, API } = useAuth();

  const [facility, setFacility] = useState(null);
  const [supplies, setSupplies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const canWrite = WRITE_ROLES.includes(user?.role);
  const canRestore = user?.role === 'super_admin';

  const loadSupplies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ facility_id: facilityId });
      if (expiringOnly) params.set('expiring_within_days', '30');
      if (showDeleted && canRestore) params.set('include_deleted', 'true');

      const [sRes, cRes] = await Promise.all([
        API.get(`/supplies?${params}`),
        API.get('/supplies/categories'),
      ]);
      setSupplies(sRes.data);
      setCategories(cRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You don't have access to this facility's supplies.");
      } else {
        setError('Failed to load supplies: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, [facilityId, expiringOnly, showDeleted, canRestore]);

  // Load facility info once (only need the name for the header)
  useEffect(() => {
    API.get('/admin/facilities')
      .then(r => setFacility(r.data.find(f => f.id === facilityId) || { name: 'Unknown Facility' }))
      .catch(() => setFacility({ name: 'Unknown Facility' }));
  }, [facilityId]);

  useEffect(() => { loadSupplies(); }, [loadSupplies]);

  // Client-side filtering for search + category (backend already handled facility/expiring/deleted)
  const filtered = supplies.filter(s => {
    if (search && !s.item_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (categoryFilter && s.category !== categoryFilter) return false;
    return true;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (supply) => {
    setEditingId(supply.id);
    setForm({
      item_name: supply.item_name || '',
      category: supply.category || '',
      quantity: supply.quantity ?? 0,
      unit: supply.unit || '',
      storage_location: supply.storage_location || '',
      lot_number: supply.lot_number || '',
      date_added: supply.date_added ? supply.date_added.split('T')[0] : '',
      expiration_date: supply.expiration_date ? supply.expiration_date.split('T')[0] : '',
      reorder_threshold: supply.reorder_threshold ?? '',
      notes: supply.notes || '',
    });
    setFormError('');
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.item_name.trim()) {
      setFormError('Item name is required.');
      return;
    }
    setSaving(true);
    setFormError('');

    // Build payload — convert empty strings to null for nullable fields,
    // and coerce numbers properly. Backend expects integers for quantity
    // and reorder_threshold, dates as 'YYYY-MM-DD' strings.
    const payload = {
      item_name: form.item_name.trim(),
      category: form.category.trim() || null,
      quantity: parseInt(form.quantity, 10) || 0,
      unit: form.unit.trim() || null,
      storage_location: form.storage_location.trim() || null,
      lot_number: form.lot_number.trim() || null,
      date_added: form.date_added || null,
      expiration_date: form.expiration_date || null,
      reorder_threshold: form.reorder_threshold === '' ? null : parseInt(form.reorder_threshold, 10),
      notes: form.notes.trim() || null,
    };

    try {
      if (editingId) {
        await API.patch(`/supplies/${editingId}`, payload);
      } else {
        await API.post('/supplies', { ...payload, facility_id: facilityId });
      }
      setModalOpen(false);
      loadSupplies();
    } catch (e) {
      setFormError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supply) => {
    if (!window.confirm(`Delete "${supply.item_name}"? It will be hidden from inventory but preserved for audit.`)) return;
    try {
      await API.delete(`/supplies/${supply.id}`);
      loadSupplies();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleRestore = async (supply) => {
    try {
      await API.post(`/supplies/${supply.id}/restore`);
      loadSupplies();
    } catch (e) {
      alert('Failed to restore: ' + (e.response?.data?.error || e.message));
    }
  };

  if (!facility) return <div className="loading-screen"><div className="spinner"/></div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:4}}>
            <Link to="/supplies" style={{color:'var(--blue)',textDecoration:'none'}}>Supplies</Link> → {facility.name}
          </div>
          <h2>{facility.name} — Emergency Supplies</h2>
          <p>{filtered.length} {filtered.length === 1 ? 'item' : 'items'}{expiringOnly && ' expiring in next 30 days'}{showDeleted && ' (including deleted)'}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {canWrite && (
            <button className="btn btn-primary" onClick={openCreate}>+ Add Supply</button>
          )}
        </div>
      </div>

      <div className="page-body">
        {error && <div className="alert-banner alert-danger">{error}</div>}

        {/* Filters row */}
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr auto auto',gap:12,alignItems:'end'}}>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Search by item name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Category</label>
              <select className="form-control" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="">All categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',padding:'0 4px',whiteSpace:'nowrap',color:'var(--text-primary)'}}>
              <input type="checkbox" checked={expiringOnly} onChange={e => setExpiringOnly(e.target.checked)} />
              Expiring soon
            </label>
            {canRestore && (
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',padding:'0 4px',whiteSpace:'nowrap',color:'var(--text-primary)'}}>
                <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
                Show deleted
              </label>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          {loading ? (
            <div style={{padding:40,textAlign:'center'}}><div className="spinner" style={{margin:'0 auto'}}/></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No supplies found</h3>
              <p>{supplies.length === 0 ? 'No supplies have been added for this facility yet.' : 'Adjust filters to see more items.'}</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Quantity</th>
                    <th>Expiration</th>
                    <th>Storage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => {
                    const status = expirationStatus(s.expiration_date);
                    const isDeleted = !!s.deleted_at;
                    const lowStock = s.reorder_threshold != null && s.quantity <= s.reorder_threshold;
                    return (
                      <tr key={s.id} style={isDeleted ? {opacity:0.55} : {}}>
                        <td style={{fontWeight:600}}>
                          {s.item_name}
                          {isDeleted && <span style={{marginLeft:8,fontSize:11,color:'var(--red)',fontWeight:600}}>(deleted)</span>}
                          {s.lot_number && <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:400,marginTop:2}}>Lot: {s.lot_number}</div>}
                        </td>
                        <td style={{fontSize:13,color:'var(--text-secondary)'}}>{s.category || '—'}</td>
                        <td>
                          <span style={lowStock ? {color:'var(--amber)',fontWeight:700} : {}}>
                            {s.quantity}{s.unit ? ` ${s.unit}` : ''}
                          </span>
                          {lowStock && <div style={{fontSize:11,color:'var(--amber)'}}>Below low-stock threshold</div>}
                        </td>
                        <td>
                          {s.expiration_date ? (
                            <>
                              <div style={expirationStyle(status)}>
                                {format(new Date(s.expiration_date), 'MMM d, yyyy')}
                              </div>
                              <div style={{fontSize:11,...expirationStyle(status)}}>{expirationLabel(s.expiration_date)}</div>
                            </>
                          ) : <span style={{color:'var(--text-secondary)'}}>—</span>}
                        </td>
                        <td style={{fontSize:13,color:'var(--text-secondary)'}}>{s.storage_location || '—'}</td>
                        <td>
                          <div style={{display:'flex',gap:6,justifyContent:'flex-end',flexWrap:'wrap'}}>
                            {!isDeleted && canWrite && (
                              <>
                                <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}>Edit</button>
                                <button
                                  className="btn btn-sm"
                                  style={{background:'#FCEBEB',color:'#A32D2D',border:'1px solid #F09595'}}
                                  onClick={() => handleDelete(s)}
                                  title="Soft delete"
                                >🗑</button>
                              </>
                            )}
                            {isDeleted && canRestore && (
                              <button className="btn btn-secondary btn-sm" onClick={() => handleRestore(s)}>Restore</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:560}}>
            <div className="modal-header">
              <h3>{editingId ? 'Edit Supply' : 'Add Supply'}</h3>
              <button className="modal-close" onClick={() => !saving && setModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Item Name <span style={{color:'var(--red)'}}>*</span></label>
                <input
                  className="form-control"
                  value={form.item_name}
                  onChange={e => setForm(f => ({...f, item_name: e.target.value}))}
                  placeholder="e.g. Bottled water 1L"
                  autoFocus
                />
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    className="form-control"
                    list="supply-categories"
                    value={form.category}
                    onChange={e => setForm(f => ({...f, category: e.target.value}))}
                    placeholder="medical, PPE, water, etc."
                  />
                  <datalist id="supply-categories">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label className="form-label">Storage Location</label>
                  <input
                    className="form-control"
                    value={form.storage_location}
                    onChange={e => setForm(f => ({...f, storage_location: e.target.value}))}
                    placeholder="Cabinet 3, Room 201"
                  />
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={form.quantity}
                    onChange={e => setForm(f => ({...f, quantity: e.target.value}))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input
                    className="form-control"
                    value={form.unit}
                    onChange={e => setForm(f => ({...f, unit: e.target.value}))}
                    placeholder="bottles, cases, lbs"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Low-stock threshold</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={form.reorder_threshold}
                    onChange={e => setForm(f => ({...f, reorder_threshold: e.target.value}))}
                    placeholder="(optional)"
                  />
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div className="form-group">
                  <label className="form-label">Date Added</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.date_added}
                    onChange={e => setForm(f => ({...f, date_added: e.target.value}))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiration Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.expiration_date}
                    onChange={e => setForm(f => ({...f, expiration_date: e.target.value}))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Lot Number</label>
                <input
                  className="form-control"
                  value={form.lot_number}
                  onChange={e => setForm(f => ({...f, lot_number: e.target.value}))}
                  placeholder="(optional)"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  placeholder="(optional)"
                />
              </div>
              {formError && <div className="field-error">{formError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Supply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
